import { createGraph } from '../graph/graphModel.js';
import { BINDING_NODE_TYPES } from '../Hasp/properties/v2/hass/index.js';
import { loadAutomationModels } from '../automation/migration/LegacyEventsToAst.js';
import { extractTargetEntities } from './hassTargets.js';
import { actionAcceptsTarget } from './hassGraph.js';
import { HASS_ROW as ROW, hassColumns } from './hassLayout.js';
import { legacyHassTag } from './hassLegacyTag.js';
const ENTITY_STRATEGIES = new Set(['state', 'mapping']);
const ENTITY_ID = /^[a-z_][a-z0-9_]*\.[a-z0-9_]+$/;
const isEntityId = value => typeof value === 'string' && ENTITY_ID.test(value.trim());

// What the HASSBuilder kept per object and the flow graph replaces.
const FLOW_KEYS = ['primaryEntity', 'automation', 'properties', 'events'];

/**
 * Convert per-object HASSBuilder configuration (`node.hass_config`) into a
 * page's Home Assistant flow graph.
 *
 * `objects` is `[{ uuid, object, hassConfig, y }]` in export (tree) order;
 * `y` is the object's page-local top. Each object's models are read with
 * loadAutomationModels, so legacy `events[]`/`properties[]` rows migrate too.
 * Compiling the result reproduces the models the HASSBuilder exported, in
 * the same order:
 *   - the Primary Entity becomes an entity node wired to the object;
 *   - each binding a binding node (its entity a wired entity node);
 *   - the rules of one event one trigger node; an unconditioned rule wires
 *     its actions to the trigger, a conditioned rule (conditions, a btnmatrix
 *     button filter, or any msgbox rule) becomes a condition node;
 *   - concrete target entities of an action become wired entity nodes.
 * Legacy openHASP popup rules (`_providerExtra`) cannot be expressed as
 * nodes and stay in `hass_config.automation`; the legacy `events[]` rows stay
 * beside them, because template-page lookups read their popup metadata
 * (they no longer export: a non-empty `automation` takes precedence). The
 * `tag` legacy rows gave the pages.jsonl record is kept as `hass_config.tag`.
 *
 * Layout: entities and bindings in two columns left of the plate, level with
 * their object; triggers, conditions, actions and the entities they act on
 * in four columns right of it. One entity node per entity and side.
 *
 * @returns {{ graph, remaining: Map<string, object> }} `remaining` is each
 *   object's hass_config without what moved into the graph.
 */
export function migrateHassConfigs(objects, { graph = createGraph(), pageId = null, plateWidth = 0 } = {}) {
    const result = { ...graph, nodes: [...graph.nodes], wires: [...graph.wires] };
    const usedIds = new Set([...result.nodes, ...result.wires].map(item => item.id));
    const uniqueId = base => {
        let id = base;
        for (let n = 2; usedIds.has(id); n++) id = `${base}-${n}`;
        usedIds.add(id);
        return id;
    };
    const addNode = (kind, type, id, x, y, data) => {
        const node = { id: uniqueId(id), kind, type, x, y, data };
        result.nodes.push(node);
        return node;
    };
    const addWire = (from, to, order) => {
        const fromId = from.node ?? from.object;
        const toId = to.node ?? to.object;
        result.wires.push({ id: uniqueId(`w-${fromId}-${toId}`), from, to, ...(order !== undefined ? { order } : {}) });
    };

    const columns = hassColumns(plateWidth);
    const left = { binding: columns.binding, entity: columns.entityIn, cursor: -Infinity, entities: new Map() };
    const right = { trigger: columns.trigger, condition: columns.condition, entity: columns.entityOut,
        action: columns.action, cursor: -Infinity, entities: new Map() };
    const entityNode = (side, entityId, y) => {
        if (!side.entities.has(entityId)) {
            side.entities.set(entityId, addNode('entity', 'entity', `en-${side === left ? 'in' : 'out'}-${entityId}`,
                side.entity, y, { entityId }));
        }
        return side.entities.get(entityId);
    };

    const remaining = new Map();
    for (const { uuid, object, hassConfig, y = 0 } of objects) {
        if (!hassConfig) continue;
        const models = loadAutomationModels({ page_id: pageId, hasp_id: object?.haspid, object, hass_config: hassConfig });
        const kept = models.filter(model => model._providerExtra);
        const rest = Object.fromEntries(Object.entries(hassConfig).filter(([key]) => !FLOW_KEYS.includes(key)));
        if (kept.length > 0) {
            rest.automation = kept.map(model => model.toJSON());
            if (Array.isArray(hassConfig.events)) rest.events = structuredClone(hassConfig.events);
        }
        const tag = legacyHassTag(hassConfig);
        if (tag !== undefined) rest.tag = tag;

        // Inbound: Primary Entity and bindings, left of the plate.
        let rowY = Math.max(y, left.cursor);
        const primary = String(hassConfig.primaryEntity ?? '').trim();
        if (isEntityId(primary)) {
            addWire({ node: entityNode(left, primary, rowY).id, port: 'state' }, { object: uuid, port: 'in' });
        } else if (primary) {
            rest.primaryEntity = hassConfig.primaryEntity;
        }
        for (const binding of models.flatMap(model => model.bindings)) {
            if (!BINDING_NODE_TYPES[binding.strategy]) continue;
            const { id, entityId, ...data } = binding.toJSON();
            const readsEntity = ENTITY_STRATEGIES.has(binding.strategy);
            const node = addNode('binding', binding.strategy, `bd-${id}`, left.binding, rowY, data);
            if (readsEntity && isEntityId(entityId)) {
                addWire({ node: entityNode(left, entityId.trim(), rowY).id, port: 'state' }, { node: node.id, port: 'entity' });
            } else if (readsEntity && entityId) {
                node.data.entityId = entityId;
            }
            addWire({ node: node.id, port: 'value' }, { object: uuid, port: 'in' });
            rowY += ROW;
        }
        left.cursor = rowY;

        // Outbound: triggers, conditions and actions, right of the plate.
        rowY = Math.max(y, right.cursor);
        const msgbox = object?.obj === 'msgbox';
        const active = models.filter(model => !model._providerExtra && model.bindings.length === 0 &&
            model.triggers.length > 0 && model.actions.length > 0);
        const triggers = new Map();
        const runs = new Map();
        const nextRun = node => { const order = runs.get(node.id) ?? 0; runs.set(node.id, order + 1); return order; };
        for (const model of models) {
            if (model._providerExtra || model.bindings.length > 0 || model.triggers.length === 0) continue;
            const trigger = model.triggers[0];
            let triggerNode = triggers.get(trigger.event);
            if (!triggerNode) {
                triggerNode = addNode('trigger', 'trigger', `tr-${trigger.id}`, right.trigger, rowY, { event: trigger.event ?? '' });
                triggers.set(trigger.event, triggerNode);
                addWire({ object: uuid, port: 'events' }, { node: triggerNode.id, port: 'source' });
            }
            const addAction = (action, from, order) => {
                let data = action.toJSON();
                delete data.id;
                const extracted = actionAcceptsTarget(data) ? extractTargetEntities(data) : null;
                if (extracted) data = extracted.action;
                const node = addNode('action', data.type === 'delay' ? 'delay' : 'service', `ha-${action.id}`, right.action, rowY, data);
                addWire(from, { node: node.id, port: 'run' }, order);
                (extracted?.entities ?? []).forEach((entityId, index) => {
                    addWire({ node: node.id, port: 'target' }, { node: entityNode(right, entityId, rowY).id, port: 'target' }, index);
                });
                rowY += ROW;
            };
            const run = { node: triggerNode.id, port: 'run' };
            const msgboxIndex = active.indexOf(model);
            const conditioned = model.conditions.length > 0 || (trigger.filter !== null && trigger.filter !== undefined) ||
                (msgbox && msgboxIndex >= 0);
            if (!conditioned) {
                for (const action of model.actions) addAction(action, run, nextRun(triggerNode));
                continue;
            }
            // A msgbox rule without conditions matches its button index (HomeAssistantGenerator._buildMsgboxEvents).
            const conditions = model.conditions.length > 0 || !msgbox
                ? model.conditions.map(condition => condition.toJSON())
                : [{ id: `${model.id}-button`, left: { kind: 'reference', name: 'val' }, operator: '==', right: { kind: 'literal', value: String(msgboxIndex) } }];
            const condition = addNode('condition', 'condition', `cd-${model.id}`, right.condition, rowY, {
                conditions, filter: trigger.filter !== null && trigger.filter !== undefined ? structuredClone(trigger.filter) : null,
            });
            addWire(run, { node: condition.id, port: 'in' }, nextRun(triggerNode));
            if (model.actions.length === 0) rowY += ROW;
            model.actions.forEach((action, index) => addAction(action, { node: condition.id, port: 'then' }, index));
        }
        right.cursor = rowY;
        remaining.set(uuid, rest);
    }
    return { graph: result, remaining };
}
